// assay.ts
// Assay - Relationship Intelligence Platform
// Design System: Deep Earth Laboratory
// Cormorant Garamond (editorial serif) + DM Mono (precision data)
// Warm earth darks, gold used sparingly like a real precious metal
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  createTheme,
  CSSVariablesResolver,
  Divider,
  Loader,
  MantineColorsTuple,
  Modal,
  NavLink,
  Paper,
  SegmentedControl,
  Select,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';

import type { ClerkProviderProps } from '@clerk/react';

import {
  DARK,
  FONT_SIZES,
  FONTS,
  HEADING_FONT_WEIGHT,
  HEADINGS,
  ORE,
  ORE_CONSTANTS,
  PATINA,
  RADIUS,
  scaleToRem,
  SCHEME,
  SPACING,
  toRem,
  type SchemeTokens,
} from '@assay/shared/tokens';
import { OreLoader } from '@/components/atoms/OreLoader/OreLoader';

type Appearance = ClerkProviderProps['appearance'];

// Palettes, scales and scheme colours live in @assay/shared so the mobile app
// consumes the same values (GOLD-452). Mantine's tuple type is structurally a
// 10-string tuple, so the shared ColorTuple satisfies it directly.
const ore: MantineColorsTuple = [...ORE];
const patina: MantineColorsTuple = [...PATINA];
const dark: MantineColorsTuple = [...DARK];

// Design token constants - CSS variables that adapt to color scheme.
// Web-only: React Native has no CSS custom properties, which is why the raw
// values these point at live in @assay/shared and this indirection does not.
// Exception: hex values in the resolver below are permitted per CLAUDE.md
export const T = {
  bg: 'var(--assay-bg)',
  surface: 'var(--assay-surface)',
  surfaceRaised: 'var(--assay-surface-raised)',
  surfaceHover: 'var(--assay-surface-hover)',
  ore: ORE_CONSTANTS.ore,
  oreLight: ORE_CONSTANTS.oreLight,
  oreDim: ORE[7],
  patina: PATINA[5],
  patinaLight: '#4fb8aa',
  textPrimary: 'var(--assay-text-primary)',
  textSecondary: 'var(--assay-text-body)',
  textMuted: 'var(--assay-text-muted)',
  border: 'var(--assay-ore-border)',
  borderHover: 'rgba(196, 152, 58, 0.28)',
};

// Maps the platform-agnostic scheme tokens onto the CSS custom properties the
// stylesheets and `T` read. One place to add a variable; the values themselves
// come from @assay/shared and are shared with mobile.
function cssVars(scheme: SchemeTokens): Record<string, string> {
  return {
    '--assay-bg': scheme.bg,
    '--assay-surface': scheme.surface,
    '--assay-surface-raised': scheme.surfaceRaised,
    '--assay-surface-hover': scheme.surfaceHover,
    '--assay-bg-input': scheme.bgInput,
    '--assay-text-primary': scheme.textPrimary,
    '--assay-text-body': scheme.textBody,
    '--assay-text-label': scheme.textLabel,
    '--assay-text-muted': scheme.textMuted,
    '--assay-ore-border': scheme.oreBorder,
    '--assay-ore-border-hover': scheme.oreBorderHover,
    '--mantine-color-body': scheme.bg,
  };
}

/** Pixel scale -> Mantine's `px` string form. */
function pxScale<K extends string>(scale: Readonly<Record<K, number>>): Record<K, string> {
  return Object.fromEntries(
    Object.entries(scale).map(([k, px]) => [k, `${px as number}px`])
  ) as Record<K, string>;
}

// Heading sizes carry lineHeight/fontWeight alongside the size, so they need a
// shape-preserving conversion rather than the flat scale helpers.
const headingSizes = Object.fromEntries(
  Object.entries(HEADINGS).map(([key, { fontSize, lineHeight, fontWeight }]) => [
    key,
    { fontSize: toRem(fontSize), lineHeight, fontWeight },
  ])
);

// CSS variables resolver - plugged into MantineProvider.
// Provides light/dark adaptive values for all semantic tokens.
export const assayCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    // Ore gold - same in both modes
    '--assay-ore': ORE_CONSTANTS.ore,
    '--assay-ore-light': ORE_CONSTANTS.oreLight,
  },
  dark: cssVars(SCHEME.dark),
  light: cssVars(SCHEME.light),
});

export const assayTheme = createTheme({
  colors: { ore, patina, dark },
  primaryColor: 'ore',
  primaryShade: 5,

  // DM Mono for all body / data text - precision instrument feel
  fontFamily: FONTS.body,
  fontFamilyMonospace: FONTS.mono,

  // Cormorant Garamond for all headings - editorial, geological journal
  headings: {
    fontFamily: FONTS.heading,
    fontWeight: HEADING_FONT_WEIGHT,
    sizes: headingSizes,
  },

  fontSizes: scaleToRem(FONT_SIZES),

  spacing: scaleToRem(SPACING),

  radius: pxScale(RADIUS),
  defaultRadius: 'sm',

  // Shadows with warm tint - depth feels like layered earth
  shadows: {
    xs: '0 1px 2px rgba(0,0,0,0.7)',
    sm: '0 2px 8px rgba(0,0,0,0.6), 0 0 0 1px rgba(196,152,58,0.06)',
    md: '0 4px 16px rgba(0,0,0,0.55), 0 0 0 1px rgba(196,152,58,0.09)',
    lg: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(196,152,58,0.12)',
    xl: '0 16px 56px rgba(0,0,0,0.5), 0 0 0 1px rgba(196,152,58,0.16)',
  },

  components: {
    Loader: Loader.extend({
      defaultProps: {
        loaders: {
          ...Loader.defaultLoaders,
          ore: (props) => <OreLoader size="sm" color={props.color} />,
        },
        type: 'ore',
      },
    }),

    Button: Button.extend({
      defaultProps: { radius: 'xs', size: 'sm' },
      styles: (theme) => ({
        root: {
          fontFamily: '"DM Mono", monospace',
          fontWeight: 400,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          transition:
            'background 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease, color 0.2s ease',
          position: 'relative',
          overflow: 'hidden',

          '&:active:not(:disabled)': { transform: 'translateY(1px)' },

          '&[data-variant="filled"]': {
            background: `linear-gradient(135deg, ${theme.colors.ore[5]} 0%, ${theme.colors.ore[6]} 100%)`,
            color: '#0c0906',
            border: '1px solid transparent',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '60%',
              height: '100%',
              background:
                'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
              transform: 'translateX(-120%) skewX(-18deg)',
              pointerEvents: 'none',
              opacity: 0,
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.colors.ore[4]} 0%, ${theme.colors.ore[5]} 100%)`,
              boxShadow: '0 0 28px rgba(196, 152, 58, 0.28)',
            },
            '&:hover::before': {
              opacity: 1,
              animation: 'assay-shimmer-sweep 0.9s ease',
            },
          },
          '&[data-variant="outline"]': {
            borderColor: 'rgba(196, 152, 58, 0.3)',
            color: theme.colors.ore[4],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(196, 152, 58, 0.08)',
              borderColor: 'rgba(196, 152, 58, 0.55)',
              boxShadow: '0 0 18px rgba(196, 152, 58, 0.08)',
            },
          },
          '&[data-variant="subtle"]': {
            color: theme.colors.ore[4],
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(196, 152, 58, 0.08)' },
          },
          '&[data-variant="light"]': {
            backgroundColor: 'rgba(196, 152, 58, 0.08)',
            color: theme.colors.ore[4],
            '&:hover': { backgroundColor: 'rgba(196, 152, 58, 0.14)' },
          },
        },
      }),
    }),

    Card: Card.extend({
      defaultProps: { radius: 'xs', withBorder: false, padding: 'lg' },
      styles: () => ({
        root: {
          backgroundColor: 'var(--assay-surface)',
          border: '1px solid var(--assay-ore-border)',
          backgroundImage:
            'radial-gradient(ellipse at 100% 0%, rgba(196, 152, 58, 0.03) 0%, transparent 50%)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          '&[data-hover="true"]:hover': {
            transform: 'translateY(-1px)',
            borderColor: 'var(--assay-ore-border-hover)',
            boxShadow:
              '0 6px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(196,152,58,0.12), 0 0 32px rgba(196,152,58,0.08)',
          },
        },
      }),
    }),

    Paper: Paper.extend({
      defaultProps: { radius: 'xs', withBorder: false, p: 'md' },
      styles: () => ({
        root: {
          backgroundColor: 'var(--assay-surface)',
          border: '1px solid var(--assay-ore-border)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          '&[data-hover="true"]:hover': {
            transform: 'translateY(-1px)',
            borderColor: 'var(--assay-ore-border-hover)',
            boxShadow:
              '0 6px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(196,152,58,0.12), 0 0 32px rgba(196,152,58,0.08)',
          },
        },
      }),
    }),

    Modal: Modal.extend({
      defaultProps: { radius: 'sm' },
      styles: () => ({
        content: { backgroundColor: 'var(--assay-surface)' },
        header: { backgroundColor: 'var(--assay-surface)' },
      }),
    }),

    TextInput: TextInput.extend({
      defaultProps: { radius: 'xs' },
      styles: () => ({
        input: {
          backgroundColor: 'var(--assay-bg-input)',
          borderColor: 'var(--assay-ore-border)',
          color: 'var(--assay-text-primary)',
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.8rem',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          '&:hover:not(:focus)': { borderColor: 'var(--assay-ore-border-hover)' },
          '&:focus': {
            borderColor: 'var(--mantine-color-ore-5)',
            boxShadow:
              '0 0 0 3px rgba(196, 152, 58, 0.14), 0 0 18px rgba(196, 152, 58, 0.08) inset',
          },
          '&::placeholder': { color: 'var(--assay-text-muted)' },
        },
        label: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.6rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--assay-text-label)',
          marginBottom: '6px',
        },
      }),
    }),

    Textarea: Textarea.extend({
      defaultProps: { radius: 'xs' },
      styles: () => ({
        input: {
          backgroundColor: 'var(--assay-bg-input)',
          borderColor: 'var(--assay-ore-border)',
          color: 'var(--assay-text-primary)',
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.8rem',
          lineHeight: '1.65',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          '&:hover:not(:focus)': { borderColor: 'var(--assay-ore-border-hover)' },
          '&:focus': {
            borderColor: 'var(--mantine-color-ore-5)',
            boxShadow:
              '0 0 0 3px rgba(196, 152, 58, 0.14), 0 0 18px rgba(196, 152, 58, 0.08) inset',
          },
          '&::placeholder': { color: 'var(--assay-text-muted)' },
        },
        label: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.6rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--assay-text-label)',
          marginBottom: '6px',
        },
      }),
    }),

    Select: Select.extend({
      defaultProps: { radius: 'xs' },
      styles: (theme) => ({
        input: {
          backgroundColor: 'var(--assay-bg-input)',
          borderColor: 'var(--assay-ore-border)',
          color: 'var(--assay-text-primary)',
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.8rem',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover:not(:focus)': { borderColor: 'var(--assay-ore-border-hover)' },
          '&:focus': {
            borderColor: theme.colors.ore[5],
            boxShadow: '0 0 0 3px rgba(196, 152, 58, 0.14)',
          },
        },
        label: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.6rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--assay-text-label)',
        },
        dropdown: {
          backgroundColor: 'var(--assay-surface)',
          border: '1px solid var(--assay-ore-border)',
        },
        option: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.8rem',
          '&[data-selected]': {
            backgroundColor: 'rgba(196, 152, 58, 0.15)',
            color: theme.colors.ore[4],
          },
          '&[data-hovered]': { backgroundColor: 'rgba(196, 152, 58, 0.08)' },
        },
      }),
    }),

    Title: Title.extend({
      styles: () => ({
        root: {
          color: 'var(--assay-text-primary)',
          letterSpacing: '-0.01em',
        },
      }),
    }),

    Text: Text.extend({
      styles: () => ({
        root: {
          color: 'var(--assay-text-body)',
          fontFamily: '"DM Mono", monospace',
          lineHeight: '1.65',
        },
      }),
    }),

    Badge: Badge.extend({
      defaultProps: { radius: 'xs' },
      styles: (theme) => ({
        root: {
          fontFamily: '"DM Mono", monospace',
          fontWeight: 400,
          letterSpacing: '0.1em',
          fontSize: '0.55rem',
          textTransform: 'uppercase',
          transition: 'transform 0.15s ease, filter 0.15s ease, background-color 0.2s ease',
          '&[data-clickable="true"]': {
            cursor: 'pointer',
            '&:hover': { transform: 'scale(1.03)', filter: 'brightness(1.1)' },
          },
          '&[data-variant="filled"]': {
            backgroundColor: theme.colors.ore[5],
            color: '#0c0906',
          },
          '&[data-variant="outline"]': {
            borderColor: 'rgba(196, 152, 58, 0.35)',
            color: theme.colors.ore[4],
          },
          '&[data-variant="light"]': {
            backgroundColor: 'rgba(196, 152, 58, 0.1)',
            color: theme.colors.ore[4],
          },
        },
      }),
    }),

    Divider: Divider.extend({
      styles: () => ({
        root: { borderColor: 'var(--assay-ore-border)' },
      }),
    }),

    Tabs: Tabs.extend({
      styles: (theme) => ({
        tab: {
          fontFamily: '"DM Mono", monospace',
          fontWeight: 400,
          fontSize: '0.65rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--assay-text-label)',
          transition: 'color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
          '&[data-active]': {
            color: theme.colors.ore[4],
            borderColor: theme.colors.ore[5],
          },
          '&:hover': {
            color: 'var(--assay-text-primary)',
            backgroundColor: 'rgba(196, 152, 58, 0.05)',
          },
        },
      }),
    }),

    SegmentedControl: SegmentedControl.extend({
      defaultProps: { radius: 'xs' },
      styles: (theme) => ({
        root: {
          backgroundColor: 'var(--assay-bg-input)',
          border: '1px solid var(--assay-ore-border)',
          padding: '3px',
        },
        // The sliding active pill.
        indicator: {
          backgroundColor: 'var(--assay-surface-raised)',
          border: `1px solid ${theme.colors.ore[5]}`,
          boxShadow: 'none',
        },
        label: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.65rem',
          fontWeight: 400,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--assay-text-label)',
          transition: 'color 0.2s ease',
          '&[data-active]': {
            color: theme.colors.ore[4],
          },
          '&:hover:not([data-active])': {
            color: 'var(--assay-text-primary)',
          },
        },
      }),
    }),

    NavLink: NavLink.extend({
      styles: (theme) => ({
        root: {
          position: 'relative',
          transition:
            'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, padding-left 0.2s ease',
          borderRadius: '3px',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '20%',
            bottom: '20%',
            width: '2px',
            background: `linear-gradient(to bottom, transparent 0%, ${theme.colors.ore[5]} 50%, transparent 100%)`,
            transform: 'scaleY(0)',
            transformOrigin: 'center',
            transition: 'transform 0.18s ease',
            pointerEvents: 'none',
          },
          '&:hover': {
            backgroundColor: 'rgba(196, 152, 58, 0.06)',
          },
          '&:hover::before': {
            transform: 'scaleY(0.7)',
            background: `linear-gradient(to bottom, transparent 0%, ${theme.colors.ore[4]}55 50%, transparent 100%)`,
          },
          '&[data-active]': {
            backgroundColor: 'rgba(196, 152, 58, 0.1)',
            color: theme.colors.ore[4],
          },
          '&[data-active]::before': {
            transform: 'scaleY(1)',
            background: `linear-gradient(to bottom, transparent 0%, ${theme.colors.ore[5]} 50%, transparent 100%)`,
          },
        },
        label: {
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.7rem',
          letterSpacing: '0.04em',
          transition: 'color 0.2s ease',
        },
        section: {
          transition: 'color 0.2s ease, transform 0.2s ease',
        },
      }),
    }),

    ActionIcon: ActionIcon.extend({
      styles: () => ({
        root: {
          transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
          '&:hover:not(:disabled)': {
            backgroundColor: 'rgba(196, 152, 58, 0.08)',
            transform: 'scale(1.04)',
          },
          '&:active:not(:disabled)': { transform: 'scale(0.97)' },
        },
      }),
    }),

    Anchor: Anchor.extend({
      styles: (theme) => ({
        root: {
          transition: 'color 0.15s ease, text-decoration-color 0.15s ease, text-shadow 0.2s ease',
          textDecorationColor: 'rgba(196, 152, 58, 0.35)',
          textUnderlineOffset: '0.2em',
          '&:hover': {
            color: theme.colors.ore[4],
            textDecorationColor: theme.colors.ore[4],
          },
        },
      }),
    }),
  },

  other: {
    // Ore values (fixed - same in both color schemes)
    oreGold: '#c4983a',
    oreRefined: '#e8b84b',
    oreDim: '#6b4f18',

    // Patina accent
    patinaBase: '#3a8c80',

    // Grade tier colors (fixed)
    gradeNugget: '#e8b84b',
    gradeVein: '#c4983a',
    gradeSeam: '#9a7426',
    gradeProspect: '#6b5c3e',

    // Adaptive tokens - use CSS variables in inline styles so they respond to color scheme
    oreBorder: 'var(--assay-ore-border)',
    oreBorderHover: 'var(--assay-ore-border-hover)',

    // Timing
    transitionFast: '0.12s ease',
    transitionBase: '0.2s ease',
    transitionSlow: '0.4s ease',
    pageTransition: '0.4s ease',
  },
});

/**
 * `<UserButton showName>` in the navbar footer renders the user's email as its
 * identifier. Clerk lets that text set the button's intrinsic width, so a long
 * address pushes the control out of the navbar. Clamp it to the container and
 * ellipsise instead.
 */
const userButtonElements: NonNullable<Appearance['elements']> = {
  userButtonBox: {
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  userButtonOuterIdentifier: {
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const assayClerkTheme: Appearance = {
  theme: 'simple',
  elements: userButtonElements,
  variables: {
    colorBackground: '#14110a',
    colorInput: '#0c0906',
    colorMuted: '#221e14',
    colorForeground: '#f0ead8',
    colorMutedForeground: '#9a8470',
    colorPrimaryForeground: '#0c0906',
    colorPrimary: '#c4983a',
    colorBorder: 'rgba(196, 152, 58, 0.15)',
    colorNeutral: '#f0ead8',
    colorDanger: '#c0392b',
    colorSuccess: '#3a8c80',
    fontFamily: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
    fontFamilyButtons: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
    fontSize: '13px',
    borderRadius: '2px',
    spacing: '14px',
  },
};

export const assayClerkThemeLight: Appearance = {
  theme: 'simple',
  elements: userButtonElements,
  variables: {
    colorBackground: '#f5f0e4',
    colorInput: '#faf7ed',
    colorMuted: '#ece5cc',
    colorForeground: '#1c1508',
    colorMutedForeground: '#6b5a40',
    colorPrimaryForeground: '#1c1508',
    colorPrimary: '#c4983a',
    colorBorder: 'rgba(196, 152, 58, 0.22)',
    colorNeutral: '#1c1508',
    colorDanger: '#c0392b',
    colorSuccess: '#3a8c80',
    fontFamily: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
    fontFamilyButtons: '"DM Mono", "Menlo", "Monaco", "Consolas", monospace',
    fontSize: '13px',
    borderRadius: '2px',
    spacing: '14px',
  },
};
